import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { FormPrevious, FormNext } from 'grommet-icons';
import uniqid from 'uniqid';
import { AppButton, Spinning } from '@Components/Control';
import NoAuthLayout from '@Components/Layout/NoAuth';
import { useAppTheme } from '@Components/Context';
import { useDispatch } from 'react-redux';
import { downloadPrefillAgreementPreview } from '@Actions/paymentActions';
import { ProductFormMultipleTextInputs } from '@Components/Partial/Product/NewSearch';
import {
  getPrefillContactState,
  getPrefillAccountStepMode,
  getPrefillLockedFields,
  buildPrefillSubmissionPayload,
} from '@Helpers/prefillOnboardingUtils';
import OnboardingStepper from './OnboardingStepper';
import { BasicDetailsStep } from './Steps';
import PrefillAgreementStep from './Steps/PrefillAgreementStep';
import PrefillPricingStep from './Steps/PrefillPricingStep';
import { StyledRaisedContainer } from './StyledOnboardingComponents';

const STEP_KEYS = {
  ACCOUNT: 'account',
  SUMMARY: 'summary',
  ALLOWLIST: 'allowlist',
  AGREEMENT: 'agreement',
};

const ALL_STEPS = [
  { key: STEP_KEYS.ACCOUNT, label: 'Account Details', disabled: false },
  { key: STEP_KEYS.SUMMARY, label: 'Services & Pricing', disabled: false },
  { key: STEP_KEYS.ALLOWLIST, label: 'Channel Allowlist', disabled: false },
  { key: STEP_KEYS.AGREEMENT, label: 'Agreement & Signature', disabled: false },
];

const hasCidProduct = (prefillData) => (prefillData?.products || []).some(
  (p) => p.productType === 'cid',
);

const isValidEmail = (email) => {
  if (!email?.trim()) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

/**
 * Onboarding wizard for customers arriving via an admin-generated prefill link.
 * Shows 2–3 steps depending on how much the admin pre-filled:
 *   1. Account Details (conditional — omitted when admin filled all required fields)
 *   2. Services & Pricing summary (always shown)
 *   3. Agreement & Signature (always shown)
 */
const PrefillOnboardingWizard = ({
  small,
  size,
  loading,
  submitLoading,
  initiateSubmit,
  prefillToken,
  prefillData,
  prefillLoading = false,
}) => {
  const { theme: { page, button } } = useAppTheme();

  const accountStepMode = React.useMemo(
    () => getPrefillAccountStepMode(prefillData),
    [prefillData],
  );

  const lockedFields = React.useMemo(
    () => getPrefillLockedFields(prefillData),
    [prefillData],
  );

  const isCid = hasCidProduct(prefillData);

  // If all fields are pre-filled, skip account step and go straight to pricing summary.
  const [activeStep, setActiveStep] = React.useState(
    accountStepMode === 'none' ? STEP_KEYS.SUMMARY : STEP_KEYS.ACCOUNT,
  );

  // Account step is conditional; allowlist step only when CID is in the plan.
  const visibleSteps = ALL_STEPS.filter((s) => {
    if (s.key === STEP_KEYS.ACCOUNT && accountStepMode === 'none') return false;
    if (s.key === STEP_KEYS.ALLOWLIST && !isCid) return false;
    return true;
  });

  const [basicDetails, setBasicDetails] = React.useState(
    () => getPrefillContactState(prefillData),
  );
  const [allowlistChannels, setAllowlistChannels] = React.useState([]);

  const [signatureBase64, setSignatureBase64] = React.useState('');
  const [signatureSubmitted, setSignatureSubmitted] = React.useState(false);
  const [finalSubmitted, setFinalSubmitted] = React.useState(false);
  const [downloadingAgreement, setDownloadingAgreement] = React.useState(false);
  const dispatch = useDispatch();

  const scrollContainerRef = React.useRef(null);
  React.useEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [activeStep]);

  // Sync step + contact state once prefillData arrives (it loads after initial render)
  React.useEffect(() => {
    if (!prefillData) return;
    setBasicDetails(getPrefillContactState(prefillData));
    if (getPrefillAccountStepMode(prefillData) === 'none') {
      setActiveStep(STEP_KEYS.SUMMARY);
    }
  }, [prefillData]);

  // --- Validation ---
  const isAccountStepValid = isValidEmail(basicDetails.email)
    && basicDetails.fullName?.trim()
    && basicDetails.companyName?.trim();

  const canGoNext = () => {
    if (activeStep === STEP_KEYS.ACCOUNT) return !!isAccountStepValid;
    return true;
  };

  const canSkip = activeStep === STEP_KEYS.ACCOUNT && accountStepMode === 'skip';

  // --- Navigation ---
  const goNext = () => {
    if (activeStep === STEP_KEYS.ACCOUNT) setActiveStep(STEP_KEYS.SUMMARY);
    else if (activeStep === STEP_KEYS.SUMMARY) setActiveStep(isCid ? STEP_KEYS.ALLOWLIST : STEP_KEYS.AGREEMENT);
    else if (activeStep === STEP_KEYS.ALLOWLIST) setActiveStep(STEP_KEYS.AGREEMENT);
  };

  const goBack = () => {
    if (activeStep === STEP_KEYS.AGREEMENT) setActiveStep(isCid ? STEP_KEYS.ALLOWLIST : STEP_KEYS.SUMMARY);
    else if (activeStep === STEP_KEYS.ALLOWLIST) setActiveStep(STEP_KEYS.SUMMARY);
    else if (activeStep === STEP_KEYS.SUMMARY && accountStepMode !== 'none') {
      setActiveStep(STEP_KEYS.ACCOUNT);
    }
  };

  const goSkip = () => setActiveStep(STEP_KEYS.SUMMARY);

  // --- Signature handlers ---
  const handleSignatureSubmit = (base64) => {
    setSignatureBase64(base64);
    setSignatureSubmitted(true);
  };

  const handleSignatureReset = () => {
    setSignatureBase64('');
    setSignatureSubmitted(false);
  };

  // --- Agreement preview download ---
  const handleDownloadAgreement = async () => {
    setDownloadingAgreement(true);
    await dispatch(downloadPrefillAgreementPreview(prefillToken));
    setDownloadingAgreement(false);
  };

  // --- Final submission ---
  const handleFinalSubmit = () => {
    if (!signatureSubmitted || !signatureBase64) return;
    setFinalSubmitted(true);
    const payload = buildPrefillSubmissionPayload(prefillToken, basicDetails, signatureBase64);
    const filledAllowlist = allowlistChannels.filter((c) => c && c.trim());
    if (filledAllowlist.length > 0) {
      // eslint-disable-next-line camelcase
      payload.allowlist_channels = filledAllowlist;
    }
    initiateSubmit(payload);
  };

  const submitDisabled = !signatureSubmitted || loading || submitLoading;

  const noAuthLayoutProps = {
    removeLowerPad: true,
    altHeaderStyle: true,
    seoConfig: null,
    small,
    title: 'Complete your onboarding',
    subTitle: 'Review your details and sign the agreement to get started.',
    customWidth: small ? '100%' : '80vw',
    customGap: '1rem',
    justifyContent: 'start',
    contentTopMargin: '5rem',
    bottomPad: '2rem',
  };

  if (prefillLoading) {
    return (
      <NoAuthLayout {...noAuthLayoutProps}>
        <Box flex align="center" justify="center" gap="1rem" pad="2rem">
          <Spinning size="large" color={page.highlight} />
          <Text size="1rem" color={page.secondaryText}>
            Validating your onboarding link…
          </Text>
        </Box>
      </NoAuthLayout>
    );
  }

  if (!prefillData) {
    return (
      <NoAuthLayout {...noAuthLayoutProps}>
        <Box flex align="center" justify="center" gap="1rem" pad="2rem">
          <Text weight={600} size="1.25rem" color={page.primaryText}>
            Invalid or expired link
          </Text>
          <Text size="0.95rem" color={page.secondaryText} textAlign="center">
            This onboarding link is no longer valid. Please contact your account manager
            or email&nbsp;
            <a
              href="mailto:contact@redflagai.co"
              style={{ color: page.altHighlight, textDecoration: 'underline' }}
            >
              contact@redflagai.co
            </a>
            &nbsp;for a new link.
          </Text>
        </Box>
      </NoAuthLayout>
    );
  }

  // --- Stepper config ---
  // Forward steps are only locked when the account step has unfilled required fields.
  // If mode is 'skip' (optional fields missing) or 'none', all steps are freely navigable.
  const visibleStepKeys = visibleSteps.map((s) => s.key);
  const activeVisibleIndex = visibleStepKeys.indexOf(activeStep);
  const isAccountBlockingProgress = activeStep === STEP_KEYS.ACCOUNT && accountStepMode === 'required';
  const stepperSteps = visibleSteps.map((s, idx) => ({
    ...s,
    disabled: isAccountBlockingProgress && idx > activeVisibleIndex,
    tooltip: (isAccountBlockingProgress && idx > activeVisibleIndex)
      ? 'Please complete the required account details to continue'
      : '',
  }));

  // --- Step content ---
  const renderStepContent = () => {
    if (activeStep === STEP_KEYS.ACCOUNT) {
      return (
        <BasicDetailsStep
          small={small}
          basicDetails={basicDetails}
          setBasicDetails={setBasicDetails}
          lockedFields={lockedFields}
        />
      );
    }
    if (activeStep === STEP_KEYS.SUMMARY) {
      return (
        <PrefillPricingStep
          small={small}
          products={prefillData?.products || []}
          extraItems={prefillData?.extraItems || []}
          currency={prefillData?.currency || 'USD'}
        />
      );
    }
    if (activeStep === STEP_KEYS.ALLOWLIST) {
      return (
        <Box gap="1rem">
          <Box gap="0.25rem">
            <Text weight={600} size="1.25rem" color={page.primaryText}>Channel Allowlist</Text>
            <Text size="0.85rem" color={page.secondaryText}>
              This step is optional. Add any YouTube channels our system should never file a
              Content ID claim or DMCA takedown against — for example, your secondary channels,
              official collaborators, or licensed re-uploaders.
            </Text>
          </Box>
          <Box
            round="var(--app-radius)"
            pad="1rem"
            background={page.cardBg}
            gap="0.5rem"
            border={{ color: page.tableBorder, size: '1px' }}
          >
            <Text size="0.85rem" weight={600} color={page.primaryText}>
              Why add channels here?
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              Our Content ID system monitors YouTube for unauthorized use of your content.
              Channels added to this list are permanently excluded from claims — protecting
              your secondary channels, collaborators, and any licensed uploaders from
              accidental takedowns.
            </Text>
          </Box>
          <ProductFormMultipleTextInputs
            value={allowlistChannels}
            handleFormValues={(updated) => setAllowlistChannels(updated)}
            onPendingInputChange={() => {}}
            placeholder="https://www.youtube.com/@channelname"
            maxInputs={50}
            focusHighlight={page.focusHighlight}
          />
          <Text size="0.8rem" color={page.secondaryText}>
            You can also manage this list anytime from your dashboard after onboarding.
          </Text>
        </Box>
      );
    }
    if (activeStep === STEP_KEYS.AGREEMENT) {
      return (
        <PrefillAgreementStep
          small={small}
          submitLoading={submitLoading}
          basicDetails={basicDetails}
          terms={prefillData?.terms}
          exhibitAData={{
            products: prefillData?.products || [],
            extraItems: prefillData?.extraItems || [],
            currency: prefillData?.currency || 'USD',
          }}
          onSignatureSubmit={handleSignatureSubmit}
          onSignatureReset={handleSignatureReset}
          signatureSubmitted={signatureSubmitted}
          signaturePreview={signatureBase64}
          finalSubmitted={finalSubmitted}
          isAgency={prefillData?.accountType === 'agency'}
          onDownloadAgreement={handleDownloadAgreement}
          downloadingAgreement={downloadingAgreement}
        />
      );
    }
    return null;
  };

  const showBack = activeStep === STEP_KEYS.AGREEMENT
    || activeStep === STEP_KEYS.ALLOWLIST
    || (activeStep === STEP_KEYS.SUMMARY && accountStepMode !== 'none');
  const showNext = activeStep === STEP_KEYS.ACCOUNT
    || activeStep === STEP_KEYS.SUMMARY
    || activeStep === STEP_KEYS.ALLOWLIST;
  const showFinish = activeStep === STEP_KEYS.AGREEMENT;

  return (
    <NoAuthLayout
      removeLowerPad
      altHeaderStyle
      seoConfig={null}
      small={small}
      title="Complete your onboarding"
      subTitle="Review your details and sign the agreement to get started."
      customWidth={small ? '100%' : '80vw'}
      customGap="1rem"
      justifyContent="start"
      contentTopMargin="5rem"
      bottomPad="2rem"
    >
      <Box
        direction="column"
        gap="1rem"
        width="100%"
        height="100%"
        style={{ flex: '1 1 0%', minHeight: 0 }}
      >
        {visibleSteps.length > 1 && (
          <StyledRaisedContainer
            width="100%"
            bgColor={page.altBg}
            style={{ flexShrink: 0 }}
          >
            <OnboardingStepper
              key={uniqid()}
              small={small}
              size={size}
              steps={stepperSteps}
              activeKey={activeStep}
              onStepClick={(key) => {
                const step = stepperSteps.find((s) => s.key === key);
                if (step && !step.disabled) setActiveStep(key);
              }}
              buttonJustify="start"
            />
          </StyledRaisedContainer>
        )}
        <StyledRaisedContainer
          ref={scrollContainerRef}
          bgColor={page.altBg}
          pad="0.5rem"
          gap="1rem"
          width="100%"
          className="onboarding-scroll-container"
          style={{ maxHeight: small ? '80vh' : '60vh', flex: '1 1 auto', overflowY: 'scroll' }}
        >
          <Box style={{ minHeight: 'fit-content', flex: '1 1 auto', display: 'flex' }}>
            {renderStepContent()}
          </Box>
        </StyledRaisedContainer>
        <Box
          direction={small ? 'column' : 'row'}
          justify="end"
          align="center"
          width="100%"
          gap={small ? '1rem' : 0}
          style={{ flexShrink: 0 }}
        >
          <Box
            direction="row"
            gap="1rem"
            align="center"
            justify="end"
          >
            {showBack && (
              <AppButton
                overrideHover
                onClick={goBack}
                level="dynamicSmall"
                bgColor={page.altBg}
                color={page.primaryText}
                borderColor={button.border}
                fontWeight={600}
                style={{ border: `1px solid ${button.border}`, minWidth: 'fit-content' }}
                label={(
                  <Box direction="row" gap="0.25rem" align="center" pad={{ right: '0.5rem' }}>
                    <Box pad={{ top: '0.15rem' }}>
                      <FormPrevious size="1.25rem" color={page.primaryText} />
                    </Box>
                    <Text color={page.primaryText}>Back</Text>
                  </Box>
                )}
              />
            )}
            {canSkip && (
              <AppButton
                overrideHover
                onClick={goSkip}
                level="dynamicSmall"
                bgColor={page.altBg}
                color={page.secondaryText}
                borderColor={button.border}
                fontWeight={600}
                style={{ border: `1px solid ${button.border}`, minWidth: 'fit-content' }}
                label={<Text color={page.secondaryText}>Skip</Text>}
              />
            )}
            {showNext && (
              <AppButton
                overrideHover
                onClick={goNext}
                disabled={!canGoNext()}
                level="dynamicSmall"
                color={button.primaryText}
                bgColor={button.primaryBg}
                style={{ minWidth: 'fit-content' }}
                fontWeight={600}
                label={(
                  <Box direction="row" gap="0.25rem" align="center" pad={{ left: '0.5rem' }}>
                    <Text>Next</Text>
                    <Box pad={{ top: '0.15rem' }}>
                      <FormNext size="1.25rem" color={button.primaryText} />
                    </Box>
                  </Box>
                )}
              />
            )}
            {showFinish && (
              <AppButton
                overrideHover
                onClick={handleFinalSubmit}
                level="dynamicSmall"
                disabled={submitDisabled}
                bgColor={button.primaryBg}
                color={button.primaryText}
                fontWeight={600}
                style={{ minWidth: 'fit-content' }}
                label={submitLoading ? (
                  <Box direction="row" gap="0.5rem" align="center" pad={{ horizontal: '0.5rem' }}>
                    <Spinning size="small" color={button.primaryText} />
                    <Text color={button.primaryText}>Submitting...</Text>
                  </Box>
                ) : 'Finish'}
              />
            )}
          </Box>
        </Box>
      </Box>
    </NoAuthLayout>
  );
};

PrefillOnboardingWizard.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  initiateSubmit: PropTypes.func.isRequired,
  prefillToken: PropTypes.string.isRequired,
  prefillData: PropTypes.shape({
    submissionId: PropTypes.string,
    accountType: PropTypes.string,
    currency: PropTypes.string,
    contact: PropTypes.shape({
      email: PropTypes.string,
      fullName: PropTypes.string,
      companyName: PropTypes.string,
      phone: PropTypes.string,
      country: PropTypes.string,
    }),
    products: PropTypes.arrayOf(PropTypes.shape({
      productKey: PropTypes.string,
      productLabel: PropTypes.string.isRequired,
      productType: PropTypes.string,
      plan: PropTypes.shape({ name: PropTypes.string, data: PropTypes.object }),
      config: PropTypes.object,
    })),
    extraItems: PropTypes.arrayOf(PropTypes.shape({
      kind: PropTypes.string.isRequired,
      valueType: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      frequency: PropTypes.string.isRequired,
      productKey: PropTypes.string,
    })),
    terms: PropTypes.shape({
      source: PropTypes.oneOf(['default', 'custom']),
      body: PropTypes.string,
    }),
  }),
  prefillLoading: PropTypes.bool,
};

export default PrefillOnboardingWizard;
